import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse, stringify } from "qs";

import { clearKeysFromQuery } from "@/hooks/useClearQueryStringKeys";
import { useSelector } from "@/redux/hooks";
import {
  getCountryCode,
  getIndicatedAddress,
} from "@/redux/selectors/location";
import { leaflyShopStringify } from "@/utils/qs";

import MetaTags from "@/components/MetaTags";
import ShopNoResults from "@/components/ShopNoResults";
import { ShopProps } from "@/pages/shop";

import DesktopFilterColumnUI from "../DesktopFilterColumnUI";
import DynamicFilters from "../DynamicFilters";
import MobileFilterModalUI, {
  MobileFilterModalProvider,
} from "../MobileFilterModalUI";
import AppliedChips from "./AppliedChips";
import AppliedFulfillmentChips from "./AppliedFulfillmentChips";
import CitiesNearby from "./CitiesNearby/CitiesNearby";
import FulfillmentFilters from "./FulfillmentFilters";
import Header from "./Header";
import { createHeaderString } from "./Header/Header";
import MobileMenuFilterShortcuts from "./MobileMenuFilterShortcuts";
import MobileMenuFulfillmentShortcuts from "./MobileMenuFulfillmentShortcuts";
import SEOCityLinks from "./SEOCityLinks";
import ShopItemResults from "./ShopItemResults";
import { extractFilterValues } from "./utils/filterUtils";

export const EXCLUDED_CATEGORIES = [
  "Dab & Oil Rigs",
  "Delta-8 THC cartridges and vapes",
  "Delta-8 THC Concentrates",
  "Delta-8 THC edibles",
  "Delta-8 THC topicals",
  "Hemp CBD edibles",
  "Hemp CBD Flower",
  "Hemp CBD topicals",
  "Hemp CBD tinctures",
  "Hemp CBD oil",
  "Other Products Containing Delta-8 THC",
];

export const createMetaDescriptionString = (
  selectedCategories: string[] | null,
  selectedFulfillments: string[] | null,
  indicatedAddress: string | null,
  hasLocationFilter?: boolean,
) => {
  const showTHCandCBDInDescription =
    selectedCategories?.length === 1 && !selectedFulfillments
      ? !EXCLUDED_CATEGORIES.includes(selectedCategories[0])
      : false;

  const categoriesString = selectedCategories?.length
    ? `the best ${showTHCandCBDInDescription ? "THC and CBD " : ""}${selectedCategories.join(" and ").toLowerCase()}`
    : "cannabis products";

  const fulfillmentsString = selectedFulfillments?.length
    ? `${selectedCategories?.length ? " available" : ""} for ${selectedFulfillments.join(" or ").toLowerCase()}`
    : "";

  return `Shop ${categoriesString}${fulfillmentsString}${hasLocationFilter ? ` near you in ${indicatedAddress}` : " near me."}`;
};

type UnifiedShopProps = Pick<
  ShopProps,
  | "shopItems"
  | "availableFilters"
  | "totalCount"
  | "citiesNearby"
  | "shopAdsFlagEnabled"
>;

export const UNIFIED_SHOP_PAGE_SIZE = 15;

export const getShopRobotMetaTagValue = (
  queryParams: Record<string, unknown>,
): "all" | "noindex" => {
  // Check for extra params beyond the allowed ones
  const hasExtraParams = Object.keys(queryParams).some((param) => {
    const isLocationParam = param === "location";
    const isFilterCategory =
      param === "filter" &&
      // Check if filter only contains category and nothing else
      typeof queryParams[param] === "object" &&
      Object.keys(queryParams[param] || {}).length === 1 &&
      Object.keys(queryParams[param] || {})[0] === "category";

    return !isLocationParam && !isFilterCategory;
  });

  return hasExtraParams ? "noindex" : "all";
};

const UnifiedShop: React.FC<UnifiedShopProps> = ({
  shopItems,
  totalCount,
  availableFilters,
  citiesNearby,
  shopAdsFlagEnabled,
}: UnifiedShopProps) => {
  const { replace } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const parsedQs = parse(searchParams?.toString() || "");
  const hasLocationFilter = Boolean(parsedQs.location);
  const indicatedAddress = useSelector(getIndicatedAddress);
  const [openFilterSection, setOpenFilterSection] = useState<string>();
  const countryCode = useSelector(getCountryCode);
  const isUsCountryCode = countryCode === "US";
  const isFirstRender = useRef(true);

  const { selectedCategories, selectedFulfillments } =
    extractFilterValues(parsedQs);

  const headerString = createHeaderString(
    selectedCategories,
    selectedFulfillments,
    indicatedAddress,
    hasLocationFilter,
  );
  const description = createMetaDescriptionString(
    selectedCategories,
    selectedFulfillments,
    indicatedAddress,
    hasLocationFilter,
  );

  const indexedParams = ["location", "fulfillment[]", "filter[category]"];
  const robotsValue = getShopRobotMetaTagValue(parsedQs);

  useEffect(() => {
    // show the attemptedRetailerName only once, if the user changes filters or locations this will update the header and url
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (parsedQs.attemptedRetailerName) {
      const newQuery = clearKeysFromQuery(parsedQs, ["attemptedRetailerName"]);
      const newQueryString = leaflyShopStringify(newQuery);
      replace(`${pathname}${newQueryString ? `?${newQueryString}` : ""}`);
    }
  }, [parsedQs, pathname, replace]);

  const { filter: queryFilters } = parsedQs;
  const queryStringFilters =
    typeof queryFilters == "object" && !Array.isArray(queryFilters)
      ? queryFilters
      : {};
  const numberOfFiltersSelected = Object.entries(queryStringFilters).length;

  const clearAllFiltersQueryString = stringify(
    clearKeysFromQuery(parsedQs, [
      "filter",
      "fulfillment",
      "page",
      "q",
      "sort",
    ]),
  );

  const filterQueryStringKeyNamespace = (filterName: string) =>
    `filter[${filterName}]`;

  return (
    <>
      <MetaTags
        canonicalParams={indexedParams}
        description={description}
        endcodeCanonicalParams={{
          encode: true,
          indices: false,
        }}
        preserveParamOrder={true}
        robots={robotsValue}
        title={headerString}
      />

      <Header />
      <section className="container" data-testid="filter-layout">
        <div className="grid grid-cols-1 gap-xs md:gap-xxl md:grid-cols-4 md:m-0">
          <div>
            <DesktopFilterColumnUI
              clearAllHref={{
                pathname,
                query: clearAllFiltersQueryString,
              }}
              chipsUI={
                <>
                  <AppliedFulfillmentChips />
                  <AppliedChips
                    availableFilters={availableFilters}
                    filterQueryStringKey={filterQueryStringKeyNamespace}
                  />
                </>
              }
              filtersUI={
                <>
                  <FulfillmentFilters />
                  <DynamicFilters
                    availableFilters={availableFilters}
                    defaultOpenKey={openFilterSection}
                    filterQueryStringKey={filterQueryStringKeyNamespace}
                  />
                </>
              }
            />
            <MobileFilterModalProvider>
              <MobileFilterModalUI
                clearAllHref={{
                  pathname,
                  query: clearAllFiltersQueryString,
                }}
                numberOfFiltersSelected={numberOfFiltersSelected}
                setOpenFilterSection={setOpenFilterSection}
                chipsUI={
                  <>
                    <AppliedFulfillmentChips />
                    <AppliedChips
                      availableFilters={availableFilters}
                      filterQueryStringKey={filterQueryStringKeyNamespace}
                    />
                    <MobileMenuFulfillmentShortcuts />
                    <MobileMenuFilterShortcuts
                      availableFilters={availableFilters}
                      setOpenFilterSection={setOpenFilterSection}
                      filterQueryStringKey={filterQueryStringKeyNamespace}
                    />
                  </>
                }
                filtersUI={
                  <>
                    <FulfillmentFilters sectionPadding="pl-lg" />
                    <DynamicFilters
                      availableFilters={availableFilters}
                      defaultOpenKey={openFilterSection}
                      sectionPadding="max-md:px-lg"
                      filterQueryStringKey={filterQueryStringKeyNamespace}
                    />
                  </>
                }
              />
            </MobileFilterModalProvider>
          </div>

          <div className="md:col-span-3">
            {shopItems.length > 0 ? (
              <ShopItemResults
                shopAdsFlagEnabled={shopAdsFlagEnabled}
                shopItems={shopItems}
                totalCount={totalCount}
              />
            ) : (
              <ShopNoResults
                titleText="No matching results"
                subtitleText="Try selecting fewer options or reset all filters"
                wrapperClassName="my-md"
                primaryLinkText="reset filters"
                action={{
                  pathname,
                  query: leaflyShopStringify(
                    clearKeysFromQuery(parsedQs, [
                      "filter",
                      "fulfillment",
                      "q",
                    ]),
                  ),
                }}
              />
            )}
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-xl bg-leafly-white py-xl">
        {isUsCountryCode && <CitiesNearby cities={citiesNearby} />}
        <SEOCityLinks />
      </div>
    </>
  );
};

export default UnifiedShop;
